/**
 * PrivatListPage (F1) — privater Dokumentensammler.
 *
 * Liste der eigenen privaten Dokumente mit Freitextsuche, Tag-Filter und der
 * Schnellansicht „Garantie läuft bald ab". Gescannt wird über „Scannen";
 * von hier aus ein Direktknopf, der danach „Privat" vorauswählt.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MTabs } from "@vimaana/ui";
import { api, type PrivatDok } from "../lib/api";

const TABS = [
  { id: "inbox", label: "Rechnungen", ic: "📥", to: "/inbox" },
  { id: "erfassen", label: "Scannen", ic: "📷", to: "/erfassen" },
  { id: "profil", label: "Profil", ic: "◌", to: "/profil" },
];

const AMPEL: Record<string, { farbe: string; text: string }> = {
  aktiv: { farbe: "#3fae7a", text: "Garantie aktiv" },
  bald: { farbe: "#e0a53f", text: "läuft bald ab" },
  abgelaufen: { farbe: "#e0705a", text: "abgelaufen" },
};

function fmtDatum(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

export default function PrivatListPage() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [expiring, setExpiring] = useState(false);

  const listQ = useQuery({
    queryKey: ["privat", { q, tag, expiring }],
    queryFn: () => api.listPrivat({ q: q.trim() || undefined, tag: tag || undefined, expiring }),
    staleTime: 30 * 1000,
    // Solange Clara noch ein Dokument liest, alle paar Sekunden nachsehen.
    refetchInterval: (qq) => ((qq.state.data ?? []).some((d) => d.ki_laeuft) ? 3000 : false),
  });
  const rows = listQ.data ?? [];

  // Tag-Wolke aus allen (ungefilterten) Dokumenten der aktuellen Liste.
  const alleTags = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.tags.forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [rows]);

  return (
    <div className="m-app" style={{ minHeight: "100vh", paddingBottom: 90 }}>
      <div style={{ padding: "calc(env(safe-area-inset-top) + 14px) 20px 8px" }}>
        <button
          onClick={() => nav("/profil")}
          style={{ background: "none", border: "none", color: "var(--m-ink-3, #888)", fontSize: 13, padding: 0, marginBottom: 8, cursor: "pointer" }}
        >
          ‹ Profil
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "var(--m-ink, #eee)" }}>Private Dokumente</h1>
      </div>

      {/* Suche + Erfassen */}
      <div style={{ display: "flex", gap: 8, padding: "0 16px 8px", alignItems: "center" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suchen (Titel, Tag, Notiz)…"
          autoCorrect="off" autoCapitalize="off"
          style={{
            flex: 1, padding: "10px 13px", borderRadius: 11, border: "1px solid var(--m-line, #333)",
            background: "var(--m-card, #1a1a1a)", color: "var(--m-ink, #eee)", fontSize: 14.5,
          }}
        />
        <button
          onClick={() => nav("/erfassen", { state: { cat: "privat" } })}
          style={{
            flexShrink: 0, padding: "10px 14px", borderRadius: 11, border: "none",
            background: "var(--m-accent, #6db04a)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}
        >
          ＋ Scannen
        </button>
      </div>

      {/* Filterzeile: „läuft bald ab" + Tags */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 16px 10px" }}>
        <Chip active={expiring} onClick={() => setExpiring((v) => !v)} label="⏳ läuft bald ab" farbe="#e0a53f" />
        {tag && <Chip active onClick={() => setTag("")} label={`✕ ${tag}`} farbe="var(--m-accent, #6db04a)" />}
        {!tag && alleTags.map((t) => (
          <Chip key={t} active={false} onClick={() => setTag(t)} label={t} farbe="var(--m-line, #444)" />
        ))}
      </div>

      <div style={{ padding: "0 16px" }}>
        {listQ.isLoading && <div style={{ padding: 20, color: "var(--m-ink-3, #888)" }}>Lade…</div>}
        {listQ.isError && <div style={{ padding: 20, color: "tomato" }}>{(listQ.error as Error).message}</div>}
        {!listQ.isLoading && rows.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--m-ink-3, #888)", fontSize: 13.5, lineHeight: 1.5 }}>
            {q || tag || expiring
              ? "Keine Treffer."
              : "Noch keine privaten Dokumente. Oben auf ＋ Scannen tippen — z. B. eine Garantiequittung."}
          </div>
        )}
        {rows.map((r) => <Zeile key={r.id} r={r} onOpen={() => nav(`/privat/${r.id}`)} />)}
      </div>

      <MTabs items={TABS} />
    </div>
  );
}

function Chip({ active, onClick, label, farbe }: { active: boolean; onClick: () => void; label: string; farbe: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 11px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
        border: `1px solid ${active ? farbe : "var(--m-line, #333)"}`,
        background: active ? `${farbe}22` : "transparent",
        color: active ? "var(--m-ink, #eee)" : "var(--m-ink-2, #bbb)", fontWeight: active ? 700 : 500,
      }}
    >
      {label}
    </button>
  );
}

function Zeile({ r, onOpen }: { r: PrivatDok; onOpen: () => void }) {
  const ampel = r.hat_garantie && r.garantie_status ? AMPEL[r.garantie_status] : null;
  return (
    <button
      onClick={onOpen}
      style={{
        display: "flex", gap: 12, width: "100%", textAlign: "left", alignItems: "center",
        padding: "10px 12px", marginBottom: 8, borderRadius: 12, cursor: "pointer",
        background: "var(--m-card, #1a1a1a)", border: "1px solid var(--m-line, #333)",
      }}
    >
      <img
        src={api.privatThumbUrl(r.id, 120)}
        alt=""
        style={{ width: 46, height: 46, objectFit: "cover", borderRadius: 8, background: "#fff", flexShrink: 0 }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: "var(--m-ink, #eee)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {r.titel || (r.ki_laeuft ? "Neues Dokument" : "Ohne Titel")}
        </span>
        {r.ki_laeuft && (
          <span style={{ display: "block", fontSize: 12, color: "var(--m-accent, #6db04a)", fontWeight: 600, marginTop: 2 }}>
            Clara liest …
          </span>
        )}
        <span style={{ display: "flex", flexWrap: "wrap", gap: "3px 6px", marginTop: 3 }}>
          {r.tags.slice(0, 4).map((t) => (
            <span key={t} style={{ fontSize: 10.5, color: "var(--m-ink-3, #999)", background: "var(--m-line, #2a2a2a)", padding: "1px 6px", borderRadius: 999 }}>{t}</span>
          ))}
          {r.dokumentdatum && <span style={{ fontSize: 10.5, color: "var(--m-ink-3, #888)" }}>{fmtDatum(r.dokumentdatum)}</span>}
        </span>
      </span>
      {ampel && (
        <span style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: ampel.farbe, fontWeight: 600 }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: ampel.farbe }} />
          {r.garantie_ende ? fmtDatum(r.garantie_ende) : ampel.text}
        </span>
      )}
    </button>
  );
}
